/* iter55p5 — Genre variant picker with BLEND support.
 *
 * Two modes, driven by an inline "Blend two" toggle:
 *   • single  — click a chip; last click wins (radio behaviour).
 *   • blend   — click chips to add/remove; multiple slugs merge in
 *               selection order. LATER selections override EARLIER
 *               ones on token collision, so if the author picks
 *               Mafia THEN Monster, `{{external_threat}}` resolves
 *               to Monster's "the hunters' guild".
 *
 * Renders:
 *   1. A blend toggle (only when 2+ genre variants exist).
 *   2. A row of chips. Selected chips show a small ordinal badge
 *      (1, 2, 3…) in blend mode so the precedence is legible.
 *   3. Merged `{{key}} → value` substitutions (later wins).
 *   4. Side-by-side Before/After preview of the first chapter that
 *      references any of the merged tokens.
 *
 * Contract:
 *   - Parent owns `selectedSlugs: string[]` state. `submit()` walks
 *     the array in order (early → late) and does an `Object.assign`
 *     so late-selection values override earlier ones.
 *   - Renders NOTHING when the template has no genre_variants.
 */
import React from 'react';
import { Label } from '@/components/ui/label';
import { interp } from './templateInterp';

/** Given a template + array of selected slugs, return the merged
 *  `{token: word}` dict with later selections overriding earlier
 *  ones. Exported so parent submit() and unit tests can call it. */
export function mergeGenreVariants(template, selectedSlugs) {
  const variants = template?.genre_variants || {};
  const merged = {};
  for (const slug of selectedSlugs || []) {
    const entry = variants[slug];
    if (!entry) continue;
    const kv = entry.values || entry;  // supports flat + nested library shapes
    Object.assign(merged, kv);
  }
  return merged;
}

export default function GenreVariantPicker({
  template, values, selectedSlugs, setSelectedSlugs,
}) {
  const genreVariants = template?.genre_variants || {};
  const genreSlugs = Object.keys(genreVariants);
  const [blendMode, setBlendMode] = React.useState(false);
  // iter55p6 — Show a one-time announcement bubble the first time a
  // writer opens a picker with 2+ variants. Dismisses to localStorage.
  const [showBlendHint, setShowBlendHint] = React.useState(false);
  React.useEffect(() => {
    if (genreSlugs.length < 2) return;
    try {
      if (!window.localStorage.getItem('pw:blend-hint-seen')) {
        setShowBlendHint(true);
      }
    } catch { /* SSR / disabled storage */ }
  }, [genreSlugs.length]);
  const dismissBlendHint = () => {
    setShowBlendHint(false);
    try { window.localStorage.setItem('pw:blend-hint-seen', '1'); } catch { /* noop */ }
  };

  if (genreSlugs.length === 0) return null;

  // When flipping OFF blend mode, collapse to the last-selected slug
  // so we don't leave a stale multi-selection on-screen.
  const toggleBlendMode = () => {
    setBlendMode((prev) => {
      const next = !prev;
      if (!next && selectedSlugs.length > 1) {
        setSelectedSlugs([selectedSlugs[selectedSlugs.length - 1]]);
      }
      return next;
    });
  };

  const onChipClick = (slug) => {
    if (blendMode) {
      if (selectedSlugs.includes(slug)) {
        setSelectedSlugs(selectedSlugs.filter((s) => s !== slug));
      } else {
        setSelectedSlugs([...selectedSlugs, slug]);
      }
    } else {
      // Single mode: replace.
      setSelectedSlugs([slug]);
    }
  };

  const chosen = mergeGenreVariants(template, selectedSlugs);
  const tokens = Object.keys(chosen);
  const previewChapter = tokens.length > 0
    ? (template.chapters || []).find((c) => {
        const text = `${c.title || ''} ${c.beat_note || ''}`;
        return tokens.some((k) => text.includes(`{{${k}}}`));
      })
    : null;

  // Precedence line — surfaces which genre wins on token collision.
  const precedenceLabel = selectedSlugs.length > 1
    ? selectedSlugs.map((s) => (genreVariants[s]?.label || s)).join(' base + ') + ' overlay (later wins)'
    : null;

  return (
    <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-2"
         data-testid="tvar-genre-picker">
      <div className="flex items-center justify-between gap-2">
        <Label className="text-[10.5px] uppercase tracking-wider text-amber-800 dark:text-amber-300">
          Genre variant · substitutes into every {'{{key}}'} inside the beats
        </Label>
        {genreSlugs.length >= 2 && (
          <label className="inline-flex cursor-pointer items-center gap-1 text-[10.5px] text-amber-800 dark:text-amber-300"
                 data-testid="tvar-genre-blend-toggle-label">
            <input
              type="checkbox"
              checked={blendMode}
              onChange={toggleBlendMode}
              data-testid="tvar-genre-blend-toggle"
              className="h-3 w-3 accent-amber-600"
            />
            Blend two
          </label>
        )}
      </div>

      {/* iter55p6 — First-run blend announcement. Localstorage-gated. */}
      {showBlendHint && (
        <div className="mt-1 flex items-start gap-2 rounded-sm border border-primary/40 bg-primary/5 p-2 text-[10.5px] leading-relaxed text-foreground"
             data-testid="tvar-blend-hint-bubble">
          <span className="mt-0.5 inline-block h-2 w-2 shrink-0 rounded-full bg-primary" />
          <div className="flex-1">
            <div className="font-semibold text-primary">New: Blend two genres</div>
            <div className="mt-0.5 opacity-90">
              Flip the <em>Blend two</em> toggle above to select multiple genres at
              once. Later picks override earlier ones on token collision — so
              Mafia + Monster resolves <code>{'{{external_threat}}'}</code> to Monster's
              &quot;hunters&apos; guild&quot; but keeps Mafia&apos;s
              <code> {'{{territory}}'} </code> if Monster doesn&apos;t override it.
            </div>
          </div>
          <button
            type="button"
            onClick={dismissBlendHint}
            data-testid="tvar-blend-hint-dismiss"
            className="rounded-sm px-1.5 py-0.5 text-[10px] font-semibold text-primary hover:bg-primary/10"
          >
            Got it
          </button>
        </div>
      )}

      <div className="mt-1 flex flex-wrap gap-1.5">
        {genreSlugs.map((slug) => {
          const entry = genreVariants[slug] || {};
          const label = entry.label || slug;
          const selectedIdx = selectedSlugs.indexOf(slug);
          const selected = selectedIdx !== -1;
          return (
            <button
              key={slug}
              type="button"
              onClick={() => onChipClick(slug)}
              data-testid={`tvar-genre-${slug}`}
              className={
                'inline-flex items-center gap-1 rounded-sm border px-1.5 py-0.5 text-[10.5px] transition ' +
                (selected
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border bg-card text-foreground/80 hover:border-primary')
              }
              title={Object.entries(entry.values || entry)
                .map(([k, v]) => `${k}: ${v}`).join(' · ')}
            >
              {blendMode && selected && (
                <span className="mr-0.5 inline-flex h-3 w-3 items-center justify-center rounded-full bg-primary-foreground/90 text-[8px] font-bold text-primary"
                      data-testid={`tvar-genre-order-${slug}`}>
                  {selectedIdx + 1}
                </span>
              )}
              {label}
            </button>
          );
        })}
      </div>

      {precedenceLabel && (
        <div className="mt-1 pl-1 text-[9.5px] italic text-amber-700 dark:text-amber-300"
             data-testid="tvar-genre-precedence">
          Precedence: {precedenceLabel}
        </div>
      )}

      {selectedSlugs.length > 0 && tokens.length > 0 && (
        <div className="mt-1 space-y-0.5 pl-1 text-[10px] text-amber-700 dark:text-amber-300">
          {Object.entries(chosen).map(([k, v]) => (
            <div key={k} className="flex gap-1">
              <span className="font-mono-stat opacity-70">{'{{'}{k}{'}}'}</span>
              <span className="opacity-80">→</span>
              <span>{String(v)}</span>
            </div>
          ))}
        </div>
      )}

      {previewChapter && (() => {
        const before = previewChapter.beat_note || '';
        const after = interp(before, { ...values, ...chosen });
        // Highlight the newly-injected values in the AFTER pane.
        const highlighted = tokens.reduce((s, k) => {
          const val = String(chosen[k] || '');
          if (!val) return s;
          return s.split(val).join(`__MARK__${val}__END__`);
        }, after);
        const afterLabel = selectedSlugs.length > 1
          ? 'Blend · ' + selectedSlugs.map((s) => (genreVariants[s]?.label || s)).join(' + ')
          : (genreVariants[selectedSlugs[0]]?.label || selectedSlugs[0]);
        return (
          <div className="mt-2 rounded-sm border border-amber-500/30 bg-background/60 p-2"
               data-testid="tvar-genre-preview-diff">
            <div className="mb-1 text-[9.5px] font-semibold uppercase tracking-wider text-amber-800 dark:text-amber-300">
              Preview · {previewChapter.title}
            </div>
            <div className="grid gap-2 text-[10.5px] leading-relaxed sm:grid-cols-2">
              <div>
                <div className="mb-0.5 text-[9.5px] uppercase tracking-wider text-muted-foreground">Before</div>
                <div className="rounded-sm bg-muted/40 p-1.5 line-through decoration-muted-foreground/40">{before}</div>
              </div>
              <div>
                <div className="mb-0.5 text-[9.5px] uppercase tracking-wider text-amber-800 dark:text-amber-300">
                  After · {afterLabel}
                </div>
                <div className="rounded-sm bg-amber-500/10 p-1.5">
                  {highlighted.split('__MARK__').map((seg, i) => {
                    if (i === 0) return <span key={`seg-${i}`}>{seg}</span>;
                    const [marked, rest] = seg.split('__END__');
                    return (
                      <span key={`seg-${i}-${marked}`}>
                        <mark className="bg-amber-400/60 px-0.5 font-medium not-italic">{marked}</mark>
                        {rest}
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
